import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { formatUSDT, parseUSDT, bpsToPercent } from '../../utils/format';
import { parseError } from '../../utils/ethers';
import { Spinner } from '../common/Loader';
import './Withdraw.css';

export default function WithdrawForm({ user, onDone }) {
  const { write, read } = useContracts();
  const notify = useNotify();

  const [amount, setAmount] = useState('');
  // Default to XT — highlighted primary option
  const [inXT, setInXT] = useState(true);
  const [busy, setBusy] = useState(false);
  const [feeUsdtBps, setFeeUsdtBps] = useState(0);
  const [feeXtBps, setFeeXtBps] = useState(0);
  const [minUsdt, setMinUsdt] = useState(ethers.BigNumber.from(0));
  const [minXt, setMinXt] = useState(ethers.BigNumber.from(0));
  const [xtPrice, setXtPrice] = useState(ethers.BigNumber.from(0));

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      read.storage.withdrawFeeUsdtBps().catch(() => ethers.BigNumber.from(0)),
      read.storage.withdrawFeeXtBps().catch(() => ethers.BigNumber.from(0)),
      read.storage.minWithdrawUsdtUSDT().catch(() => ethers.BigNumber.from(0)),
      read.storage.minWithdrawXtUSDT().catch(() => ethers.BigNumber.from(0)),
      read.storage.xtPriceUSDT(),
    ]).then(([feeU, feeX, minU, minX, price]) => {
      if (cancelled) return;
      setFeeUsdtBps(feeU);
      setFeeXtBps(feeX);
      setMinUsdt(minU);
      setMinXt(minX);
      setXtPrice(price);
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const feeBps = inXT ? feeXtBps : feeUsdtBps;
  const minWithdraw = inXT ? minXt : minUsdt;
  const amountBn = parseUSDT(amount);
  const feeBn = amountBn.mul(feeBps).div(10000);
  const netBn = amountBn.sub(feeBn);
  const xtBn = xtPrice.gt(0)
    ? netBn.mul(ethers.utils.parseUnits('1', 18)).div(xtPrice)
    : ethers.BigNumber.from(0);

  const pending = user ? user.pendingBalanceUSDT : ethers.BigNumber.from(0);
  // Available balance in XT equivalent (mirror of pending USD at current XT price)
  const pendingXt = xtPrice.gt(0)
    ? pending.mul(ethers.utils.parseUnits('1', 18)).div(xtPrice)
    : ethers.BigNumber.from(0);

  function setMax() {
    setAmount(ethers.utils.formatUnits(pending, 18));
  }

  async function handleWithdraw() {
    if (!write) {
      notify.error('Please connect your wallet on the correct network.');
      return;
    }
    if (amountBn.lte(0)) {
      notify.warning('Enter a valid amount.');
      return;
    }
    if (amountBn.lt(minWithdraw)) {
      notify.error(`Minimum withdrawal is ${formatUSDT(minWithdraw)} USDT.`);
      return;
    }
    if (amountBn.gt(pending)) {
      notify.error('Amount exceeds pending balance.');
      return;
    }

    setBusy(true);
    try {
      notify.info('Submitting withdrawal...');
      const tx = await write.logic.withdraw(amountBn, inXT);
      await tx.wait();
      notify.success(
        `Withdrawal successful! Received ${inXT ? formatUSDT(xtBn, 4) + ' XT' : formatUSDT(netBn) + ' USDT'}.`
      );
      setAmount('');
      if (onDone) onDone();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card withdraw-card">
      <div className="withdraw-header">
        <h2>💰 Withdraw Rewards</h2>
        <p style={{ color: 'var(--text-muted)', marginTop: 6 }}>
          Withdraw your mining and level rewards.
        </p>
      </div>

      <div className="available-box">
        <div className="available-label">Available Balance</div>
        <div className="available-value">
          {formatUSDT(pendingXt, 4)}<span className="unit">XT</span>
        </div>
        <div className="available-value" style={{ fontSize: '0.7em', opacity: 0.85, marginTop: 4 }}>
          ≈ {formatUSDT(pending)}<span className="unit">USDT</span>
        </div>
      </div>

      <label className="label">Receive as</label>
      <div className="currency-toggle">
        {/* XT — FIRST and DEFAULT (highlighted primary option) */}
        <div
          className={`currency-option currency-xt ${inXT ? 'selected recommended' : ''}`}
          onClick={() => setInXT(true)}
        >
          {inXT && <div className="rec-badge">RECOMMENDED</div>}
          <div className="cur-label">XT Token</div>
          <div className="cur-sub">Platform token · Grows with the ecosystem</div>
        </div>
        {/* USDT — SECOND */}
        <div
          className={`currency-option ${!inXT ? 'selected' : ''}`}
          onClick={() => setInXT(false)}
        >
          <div className="cur-label">USDT</div>
          <div className="cur-sub">Stablecoin</div>
        </div>
      </div>

      <div className="mb-6" style={{ marginTop: 20 }}>
        <label className="label">Amount</label>
        <div style={{ position: 'relative' }}>
          <input
            className="input"
            type="text"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          />
          <button
            type="button"
            onClick={setMax}
            style={{
              position: 'absolute', right: 12, top: '50%',
              transform: 'translateY(-50%)',
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              color: 'var(--accent)', padding: '4px 10px', borderRadius: 12,
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >
            MAX
          </button>
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
          Minimum: {formatUSDT(minWithdraw)} USDT
        </div>
      </div>

      <div style={{ padding: '16px 0', borderTop: '1px solid var(--border-soft)', marginTop: 8 }}>
        <div className="summary-row">
          <span>Gross</span>
          <span className="value">{formatUSDT(amountBn)} USDT</span>
        </div>
        <div className="summary-row">
          <span>Fee ({bpsToPercent(feeBps)})</span>
          <span className="value">-{formatUSDT(feeBn)} USDT</span>
        </div>
        <div className="summary-row">
          <span>You Receive</span>
          <span className="value">
            {inXT ? `${formatUSDT(xtBn, 4)} XT` : `${formatUSDT(netBn)} USDT`}
          </span>
        </div>
      </div>

      <button
        className="btn btn-primary btn-lg btn-block mt-6"
        onClick={handleWithdraw}
        disabled={busy || amountBn.lte(0)}
      >
        {busy ? <><Spinner /> Processing...</> : `Withdraw as ${inXT ? 'XT' : 'USDT'}`}
      </button>
    </div>
  );
}
